import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEye, FaCalendarAlt, FaUser, FaPlus, FaBlog } from "react-icons/fa";
import API from "../../api/api";
import { useUser } from "../../context/UserContext";

interface Blog {
    id: string;
    authorId: string;
    authorName: string;
    readTime: number;
    title: string;
    category: string;
    content: string;
    tags: string[];
    featuredImage: string | null;
    status: string;
    likes: number;
    comments: any[];
    createdAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    updatedAt: {
        _seconds: number;
        _nanoseconds: number;
    };
    views: number;
}

interface Comment {
    id: string;
    authorId: string;
    authorName: string;
    content: string;
    createdAt: string;  // ISO string format
}

const BlogDetail: React.FC = () => {
    const { blogId } = useParams<{ blogId: string }>();
    const navigate = useNavigate();

    const [blog, setBlog] = useState<Blog | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [commentText, setCommentText] = useState("");
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [commentError, setCommentError] = useState<string | null>(null);
    const {user} = useUser();
  

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch blog from backend API
                const response = await API.get(`/blogs/${blogId}`);

                if (response.data) {
                    setBlog(response.data.blog);
                } else {
                    setError("Blog not found");
                }
            } catch (err) {
                console.error("Failed to fetch blog:", err);
                setError("Failed to load blog post. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        if (blogId) {
            fetchBlog();
        } else {
            setError("Blog ID not provided");
            setLoading(false);
        }
    }, [blogId]);

    const handleCommentSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!commentText.trim()) {
          setCommentError("Comment cannot be empty");
          return;
        }

        if (!user?.id || !user?.firstName) {
          setCommentError("You must be logged in to comment");
          return;
        }
    
        try {
          setCommentSubmitting(true);
          setCommentError(null);
          
          const newComment: Omit<Comment, 'id' | 'createdAt'> = {
            authorId: user.id,
            authorName: user.firstName,
            content: commentText.trim()
          };
    
          // Submit comment to backend
          const response = await API.post(`/blogs/${blogId}/comment`, newComment);
          
          if (response.data?.comment) {
            // Update local state with the new comment
            if (blog) {
              setBlog({
                ...blog,
                comments: [...(blog.comments || []), response.data.comment]
              });
              setCommentText("");
            }
          } else {
            throw new Error("Failed to get comment data from response");
          }
        } catch (err) {
          console.error("Failed to submit comment:", err);
          setCommentError("Failed to post comment. Please try again.");
        } finally {
          setCommentSubmitting(false);
        }
      };
    

    const formatBlogDate = (timestamp: { _seconds: number; _nanoseconds: number }) => {
        const date = new Date(timestamp._seconds * 1000);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const formatCommentDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                throw new Error('Invalid date');
            }

            return date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
            });
        } catch (error) {
            console.error('Error formatting comment date:', error);
            return 'Invalid date';
        }
    };

    if (loading) {
        return (
            <div className="container py-5">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading blog post...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
                <button
                    onClick={() => navigate("/user-stories")}
                    className="btn btn-outline-primary mt-3"
                >
                    <FaArrowLeft className="me-2" />
                    Back to all stories
                </button>
            </div>
        );
    }

    if (!blog) {
        return (
            <div className="container py-5">
                <div className="alert alert-warning" role="alert">
                    Blog not found.
                </div>
                <button
                    onClick={() => navigate("/user-stories")}
                    className="btn btn-outline-primary mt-3"
                >
                    <FaArrowLeft className="me-2" />
                    Back to all stories
                </button>
            </div>
        );
    }

    console.log(blog);

    return (
        <div className="container py-5 blog-container">
            <div className="row justify-content-center">
                <div className="col-lg-10">
                    <div className="d-flex justify-content-between align-items-center mb-4 gap-3">
                        <button
                            onClick={() => navigate("/user-stories")}
                            className="btn btn-outline-primary d-flex align-items-center"
                        >
                            <FaArrowLeft className="me-2" />
                            Back to all stories
                        </button>
                        <div className="d-flex gap-2">
                            <button
                                onClick={() => navigate('/newblog')}
                                className="btn btn-primary d-flex align-items-center"
                            >
                                <FaPlus className="me-2" />
                                Add New Story
                            </button>
                            <button
                                onClick={() => navigate('/newblog')}
                                className="btn btn-success d-flex align-items-center"
                            >
                                <FaBlog className="me-2" />
                                New Blog
                            </button>
                        </div>
                    </div>

                    <article className="card shadow-sm border-0 rounded-4 overflow-hidden mb-5">

                        <div className="featured-image-container">
                            <img
                                src={blog.featuredImage || "https://img.freepik.com/premium-vector/gray-wavy-halftone-vector-white-background-vector_639343-98.jpg?ga=GA1.1.147156507.1748208380&semt=ais_hybrid&w=740"}
                                className="img-fluid w-100 featured-image"
                                alt={blog.title}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://upload.wikimedia.org/wikipedia/commons/d/d2/Solid_white.png?20060513000852';
                                }}
                            />
                            <div className="position-absolute top-0 start-0 m-3">
                                <span className="badge bg-dark bg-opacity-75 text-white px-3 py-2 rounded-pill d-flex align-items-center">
                                    <FaEye className="me-2" />
                                    {blog.views} views
                                </span>
                            </div>
                        </div>


                        <div className="card-body p-4 p-md-5">
                            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 text-muted small">
                                <span className="d-flex align-items-center mb-2 mb-md-0">
                                    <FaCalendarAlt className="me-2" />
                                    {formatBlogDate(blog.createdAt)}
                                </span>
                                <span className="d-flex align-items-center">
                                    <FaUser className="me-2" />
                                    By {blog.authorName}
                                </span>
                            </div>

                            <h1 className="mb-4 fw-bold">{blog.title}</h1>

                            <div className="mb-4">
                                <div className="d-flex flex-wrap gap-2 mb-4">
                                    {blog?.tags?.map((tag, index) => (
                                        <span key={index} className="badge bg-light text-dark">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="content mb-5">
                                {blog?.content?.split('\n').map((paragraph, index) => (
                                    paragraph.trim() && (
                                        <p key={index} className="mb-4 fs-5 lh-base">
                                            {paragraph}
                                        </p>
                                    )
                                ))}
                            </div>

                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center border-top border-bottom py-4 mb-4">
                                {/* <div className="d-flex align-items-center mb-3 mb-md-0">
                  <button 
                    onClick={handleLike}
                    className="btn btn-outline-danger me-3 d-flex align-items-center"
                  >
                    {isLiked ? (
                      <FaHeart className="me-2" />
                    ) : (
                      <FaRegHeart className="me-2" />
                    )}
                    {isLiked ? blog.likes + 1 : blog.likes} Likes
                  </button>
                  
                  <div className="d-flex align-items-center">
                    {renderStars((blog.likes / (blog.views || 1)) * 5)}
                  </div>
                </div> */}
                                {/* 
                <button 
                  onClick={handleShare}
                  className="btn btn-outline-primary d-flex align-items-center"
                >
                  <FaShareAlt className="me-2" />
                  Share
                </button> */}
                            </div>

                  
                                <div className="comments-section">
                                    <h5 className="mb-4">Comments ({blog?.comments?.length || 0})</h5>
                                    {blog?.comments?.length === 0 ? (
                                        <p className="text-muted">No comments yet. Be the first to comment!</p>
                                    ) : (
                                        <div className="list-group mb-4">
                                            {blog?.comments?.map((comment, index) => (
                                                <div key={index} className="list-group-item">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <strong>{comment.authorName}</strong>
                                                        <small className="text-muted">
                                                            {comment.createdAt ? 
                                                                formatCommentDate(comment.createdAt) : 
                                                                "Just now"}
                                                        </small>
                                                    </div>
                                                    <p className="mb-0">{comment.content}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <form onSubmit={handleCommentSubmit} className="mt-4">
                                        <div className="mb-3">
                                            <textarea
                                                className="form-control"
                                                rows={3}
                                                placeholder="Add your comment..."
                                                value={commentText}
                                                onChange={(e) => setCommentText(e.target.value)}
                                                required
                                                disabled={commentSubmitting}
                                            ></textarea>
                                            {commentError && (
                                                <div className="text-danger small mt-2">{commentError}</div>
                                            )}
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={commentSubmitting || !commentText.trim()}
                                        >
                                            {commentSubmitting ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Posting...
                                                </>
                                            ) : (
                                                "Post Comment"
                                            )}
                                        </button>
                                    </form>
                                </div>
                           
                        </div>
                    </article>
                </div>
            </div>

            <style>{`
        .blog-container .featured-image-container {
          position: relative;
          max-height: 500px;
          overflow: hidden;
        }
        
        .blog-container .featured-image {
          object-fit: cover;
          object-position: center;
          height: 500px;
        }
        .container{
          background: none !important;
          border: 2px solid silver;
        }
        .blog-container .content p {
          text-align: justify;
        }
        
        .btn-primary {
          background-color: #0d6efd;
          border-color: #0d6efd;
          transition: all 0.3s ease;
        }
        
        .btn-success {
          background-color: #198754;
          border-color: #198754;
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover, .btn-success:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        .btn-primary:hover {
          background-color: #0b5ed7;
          border-color: #0a58ca;
        }
        
        .btn-success:hover {
          background-color: #157347;
          border-color: #146c43;
        }
        
        .btn-outline-primary {
          transition: all 0.3s ease;
        }
        
        .btn-outline-primary:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        
        @media (max-width: 768px) {
          .blog-container .featured-image {
            height: 300px;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 1rem;
          }
          
          .d-flex.justify-content-between .btn,
          .d-flex.gap-2 {
            width: 100%;
          }
          
          .d-flex.gap-2 {
            flex-direction: column;
          }
          
          .d-flex.justify-content-between .btn {
            justify-content: center;
          }
        }
      `}</style>
        </div>
    );
};

export default BlogDetail;